import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Button,
  Flex,
  HStack,
  Text,
  useToast,
  useDisclosure,
  SimpleGrid,
  Spinner,
  Center,
  FormControl,
  Input,
  Select,
  Textarea,
  Tooltip,
  Tr,
  Td,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from "@chakra-ui/react";
import { ArrowBackIcon, DeleteIcon, EditIcon, AddIcon } from "@chakra-ui/icons";
import { FaCalendarCheck } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import { PLATFORM_ROLES, BU_ROLES } from "../../../constants/roles";
import {
  createInterviewForMatch,
  deleteMatch,
  getMatch,
  listInterviewsForMatch,
  updateInterview,
  updateMatch,
} from "../../../api/matchesApi";
import { getConsultant } from "../../../api/consultantsApi";
import {
  NEED_STATUS_LABELS,
  NEED_STATUS_LABEL_KEYS,
  CONSULTANT_STATUS_LABELS,
  CONSULTANT_STATUS_LABEL_KEYS,
  MATCH_ASSIGNMENT_STATUSES,
  MATCH_ASSIGNMENT_STATUS_LABELS,
  MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
  MATCH_PURCHASE_ORDER_STATUSES,
  MATCH_PURCHASE_ORDER_STATUS_LABELS,
  MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
  INTERVIEW_STATUSES,
  INTERVIEW_STATUS_LABELS,
  INTERVIEW_STATUS_LABEL_KEYS,
  ASSIGNMENT_STATUS_COLORS,
  PURCHASE_ORDER_STATUS_COLORS,
  INTERVIEW_STATUS_COLORS,
  getStatusLabel,
} from "../../../constants/statuses";
import {
  PageHeader,
  Panel,
  KpiStrip,
  KpiCard,
  DataTable,
  StatusBadge,
  MiniIconButton,
  EmptyState,
  FieldLabel,
} from "../../../components/Portal/ui";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";

// Token control sizing shared across the migrated portal forms/filters.
const TOKEN_INPUT_SX = {
  height: "31px !important",
  fontWeight: 600,
  bg: "white",
};

const TOKEN_SELECT_SX = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  fontSize: "12px",
  fontWeight: 600,
};

// A read-only display field: tiny uppercase label over a value line.
const ReadField = ({ label, children }) => (
  <Box minW={0}>
    <Text
      textTransform="uppercase"
      fontSize="10px"
      fontWeight="800"
      letterSpacing="0.055em"
      color="portal.muted"
      sx={{ marginBottom: "2px !important" }}
    >
      {label}
    </Text>
    <Box fontSize="13px" fontWeight="600" color="portal.text">
      {children}
    </Box>
  </Box>
);

// Skill chip matching the template's neutral pill treatment.
const SkillChip = ({ children }) => (
  <Box
    as="span"
    fontSize="11px"
    fontWeight="700"
    color="portal.text"
    bg="portal.soft"
    borderWidth="1px"
    borderColor="portal.line"
    px="8px"
    py="3px"
    borderRadius="999px"
    whiteSpace="nowrap"
  >
    {children}
  </Box>
);

const parseSkills = (raw) => {
  if (!raw) return [];
  if (Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed;
    } catch {
      return [];
    }
  }
  return [];
};

const MatchDetail = () => {
  const { matchId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { t } = useTranslation("portal");
  const { effectiveRole, selectedBusinessUnitId } = useBusinessUnit();
  const location = useLocation();

  const initialMatch = location.state?.match || null;
  const initialNeed = location.state?.need || null;

  const [matchData, setMatchData] = useState(initialMatch);
  const [need, setNeed] = useState(initialNeed);
  const [loadingMatch, setLoadingMatch] = useState(false);

  const [consultant, setConsultant] = useState(null);
  const [loadingConsultant, setLoadingConsultant] = useState(false);

  const toDateInput = (v) => {
    if (!v) return "";
    // already YYYY-MM-DD
    if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}$/.test(v)) return v;
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "";
      return d.toISOString().slice(0, 10);
    } catch {
      return "";
    }
  };

  const toDatetimeLocalInput = (v) => {
    if (!v) return "";
    // If backend returns ISO, convert to datetime-local (YYYY-MM-DDTHH:mm)
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return "";
      const pad = (n) => String(n).padStart(2, "0");
      const yyyy = d.getFullYear();
      const mm = pad(d.getMonth() + 1);
      const dd = pad(d.getDate());
      const hh = pad(d.getHours());
      const mi = pad(d.getMinutes());
      return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
    } catch {
      return "";
    }
  };

  const formatDatetimeLocalForDisplay = (v) => {
    if (!v) return "-";
    // v is likely YYYY-MM-DDTHH:mm (from tracking state)
    if (typeof v === "string" && v.includes("T")) return v.replace("T", " ");
    return String(v);
  };

  const fromDatetimeLocalToIso = (v) => {
    if (!v) return null;
    // v: YYYY-MM-DDTHH:mm
    try {
      const d = new Date(v);
      if (Number.isNaN(d.getTime())) return null;
      return d.toISOString();
    } catch {
      return null;
    }
  };

  const [tracking, setTracking] = useState({
    assignmentStatus: "",
    assignmentStartDate: "",
    assignmentEndDate: "",
    purchaseOrderStatus: "",
    purchaseOrderNumber: "",
    purchaseOrderReceivedAt: "",
    purchaseOrderEndDate: "",
    atsScore: "",
    skillsScore: "",
    clientSatisfactionScore: "",
    clientSatisfactionComment: "",
  });
  const [savingTracking, setSavingTracking] = useState(false);
  const trackingModal = useDisclosure();

  const [interviews, setInterviews] = useState([]);
  const [loadingInterviews, setLoadingInterviews] = useState(false);
  const [savingInterview, setSavingInterview] = useState(false);
  const interviewModal = useDisclosure();
  const deleteConfirmDialog = useDisclosure();
  const [interviewForm, setInterviewForm] = useState({
    id: null,
    scheduledAt: "",
    completedAt: "",
    status: "",
  });

  const canDelete = useMemo(() => {
    return (
      effectiveRole === PLATFORM_ROLES.COMPANY_ADMIN ||
      effectiveRole === BU_ROLES.ADMIN ||
      effectiveRole === BU_ROLES.MANAGER
    );
  }, [effectiveRole]);

  const fetchMatch = useCallback(async () => {
    if (!matchId) return;
    setLoadingMatch(true);
    try {
      const data = await getMatch(matchId, { buId: selectedBusinessUnitId || undefined });
      setMatchData(data);
    } catch (e) {
      setMatchData(null);
    } finally {
      setLoadingMatch(false);
    }
  }, [matchId, selectedBusinessUnitId]);

  // Always refresh match from backend (router state can be stale/incomplete)
  useEffect(() => {
    if (!matchId) return;
    fetchMatch();
  }, [fetchMatch, matchId]);

  // Keep tracking form in sync with matchData
  useEffect(() => {
    if (!matchData) return;
    setTracking({
      assignmentStatus: matchData.assignment_status || matchData.assignmentStatus || "",
      assignmentStartDate: toDateInput(matchData.assignment_start_date || matchData.assignmentStartDate),
      assignmentEndDate: toDateInput(matchData.assignment_end_date || matchData.assignmentEndDate),
      purchaseOrderStatus: matchData.purchase_order_status || matchData.purchaseOrderStatus || "",
      purchaseOrderNumber: matchData.purchase_order_number || matchData.purchaseOrderNumber || "",
      purchaseOrderReceivedAt: toDatetimeLocalInput(
        matchData.purchase_order_received_at || matchData.purchaseOrderReceivedAt
      ),
      purchaseOrderEndDate: toDateInput(
        matchData.purchase_order_end_date || matchData.purchaseOrderEndDate
      ),
      atsScore: matchData.atsScore ?? matchData.ats_score ?? "",
      skillsScore: matchData.skillsScore ?? matchData.skills_score ?? "",
      clientSatisfactionScore:
        matchData.client_satisfaction_score ?? matchData.clientSatisfactionScore ?? "",
      clientSatisfactionComment:
        matchData.client_satisfaction_comment || matchData.clientSatisfactionComment || "",
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [matchData]);

  const normalizeInterview = (raw) => {
    if (!raw) return null;
    return {
      id: raw.id,
      matchId: raw.match_id ?? raw.matchId,
      scheduledAt: raw.scheduled_at ?? raw.scheduledAt,
      completedAt: raw.completed_at ?? raw.completedAt,
      status: raw.status || "",
    };
  };

  const formatInterviewDatetime = (value) => {
    if (!value) return "-";
    const localValue = toDatetimeLocalInput(value);
    return localValue ? formatDatetimeLocalForDisplay(localValue) : "-";
  };

  const resetInterviewForm = () => {
    setInterviewForm({
      id: null,
      scheduledAt: "",
      completedAt: "",
      status: "",
    });
  };

  const openCreateInterview = () => {
    resetInterviewForm();
    interviewModal.onOpen();
  };

  const openEditInterview = (interview) => {
    if (!interview) return;
    setInterviewForm({
      id: interview.id,
      scheduledAt: toDatetimeLocalInput(interview.scheduledAt),
      completedAt: toDatetimeLocalInput(interview.completedAt),
      status: interview.status || "",
    });
    interviewModal.onOpen();
  };

  const fetchInterviews = useCallback(async () => {
    if (!matchId) return;
    setLoadingInterviews(true);
    try {
      const data = await listInterviewsForMatch(matchId, {
        buId: selectedBusinessUnitId || undefined,
      });
      const normalized = Array.isArray(data)
        ? data.map(normalizeInterview).filter(Boolean)
        : [];
      setInterviews(normalized);
    } catch (e) {
      setInterviews([]);
      toast({
        title: t("pilotMatches.detail.interviews.toasts.loadFailedTitle"),
        status: "error",
      });
    } finally {
      setLoadingInterviews(false);
    }
  }, [matchId, selectedBusinessUnitId, t, toast]);

  useEffect(() => {
    if (!matchId) return;
    fetchInterviews();
  }, [fetchInterviews, matchId]);

  useEffect(() => {
    const consultantId = matchData?.consultant_id || matchData?.consultantId;
    if (!consultantId) return;

    const fetchConsultant = async () => {
      setLoadingConsultant(true);
      try {
        const data = await getConsultant(consultantId, {
          buId: selectedBusinessUnitId || undefined,
        });
        setConsultant(data);
      } catch (e) {
        toast({
          title: t("pilotConsultants.detail.toasts.loadConsultantFailed"),
          status: "error",
        });
      } finally {
        setLoadingConsultant(false);
      }
    };

    fetchConsultant();
  }, [matchData, selectedBusinessUnitId, toast, t]);

  const handleDelete = async () => {
    if (!matchData) return;
    try {
      await deleteMatch(matchData.id, { buId: selectedBusinessUnitId || undefined });
      toast({
        title: t("pilotMatches.toasts.deleteSuccess"),
        status: "success",
      });
      navigate("/portal/asap-pilot/matches");
    } catch (e) {
      toast({
        title: t("pilotMatches.toasts.deleteFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotMatches.toasts.deleteFailedDescription"),
        status: "error",
      });
    }
  };

  const handleSaveTracking = async () => {
    if (!matchData?.id) return;
    setSavingTracking(true);
    try {
      const payload = {
        assignmentStatus: tracking.assignmentStatus || null,
        assignmentStartDate: tracking.assignmentStartDate || null,
        assignmentEndDate: tracking.assignmentEndDate || null,
        purchaseOrderStatus: tracking.purchaseOrderStatus || null,
        purchaseOrderNumber: tracking.purchaseOrderNumber || null,
        purchaseOrderReceivedAt: fromDatetimeLocalToIso(tracking.purchaseOrderReceivedAt),
        purchaseOrderEndDate: tracking.purchaseOrderEndDate || null,
        atsScore: tracking.atsScore === "" ? null : Number(tracking.atsScore),
        skillsScore: tracking.skillsScore === "" ? null : Number(tracking.skillsScore),
        clientSatisfactionScore:
          tracking.clientSatisfactionScore === ""
            ? null
            : Number(tracking.clientSatisfactionScore),
        clientSatisfactionComment: tracking.clientSatisfactionComment || null,
      };
      const updated = await updateMatch(matchData.id, payload, {
        buId: selectedBusinessUnitId || undefined,
      });
      setMatchData(updated || matchData);
      // Read-after-write to ensure we display what the backend persists/returns
      await fetchMatch();
      toast({
        title: t("pilotMatches.detail.missionTracking.toasts.saveSuccess"),
        status: "success",
      });
    } catch (e) {
      toast({
        title: t("pilotMatches.detail.missionTracking.toasts.saveFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotMatches.detail.missionTracking.toasts.saveFailedDescription"),
        status: "error",
      });
    } finally {
      setSavingTracking(false);
    }
  };

  const handleSaveInterview = async () => {
    const targetMatchId = matchData?.id || matchId;
    if (!targetMatchId) return false;

    const hasScheduled = Boolean(interviewForm.scheduledAt);
    const hasCompleted = Boolean(interviewForm.completedAt);
    const hasStatus = Boolean(interviewForm.status);

    if (!interviewForm.id && !hasScheduled && !hasCompleted) {
      toast({
        title: t("pilotMatches.detail.interviews.toasts.missingTime"),
        status: "warning",
      });
      return false;
    }

    if (interviewForm.id && !hasScheduled && !hasCompleted && !hasStatus) {
      toast({
        title: t("pilotMatches.detail.interviews.toasts.nothingToUpdate"),
        status: "warning",
      });
      return false;
    }

    setSavingInterview(true);
    try {
      const payload = {};
      if (hasScheduled) {
        payload.scheduledAt = fromDatetimeLocalToIso(interviewForm.scheduledAt);
      }
      if (hasCompleted) {
        payload.completedAt = fromDatetimeLocalToIso(interviewForm.completedAt);
      }
      if (hasStatus) {
        payload.status = interviewForm.status;
      }

      if (interviewForm.id) {
        await updateInterview(interviewForm.id, payload, {
          buId: selectedBusinessUnitId || undefined,
        });
      } else {
        await createInterviewForMatch(targetMatchId, payload, {
          buId: selectedBusinessUnitId || undefined,
        });
      }

      await fetchInterviews();
      toast({
        title: t("pilotMatches.detail.interviews.toasts.saveSuccess"),
        status: "success",
      });
      return true;
    } catch (e) {
      toast({
        title: t("pilotMatches.detail.interviews.toasts.saveFailedTitle"),
        description:
          e?.response?.data?.message ||
          t("pilotMatches.detail.interviews.toasts.saveFailedDescription"),
        status: "error",
      });
      return false;
    } finally {
      setSavingInterview(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Derived data (only valid once matchData is present, computed below render
  // guards where needed). These are safe to compute unconditionally.
  // ---------------------------------------------------------------------------
  const needSkillsTechnical = parseSkills(
    need?.skillsTechnical || need?.skills_technical
  );
  const needSkillsFunctional = parseSkills(
    need?.skillsFunctional || need?.skills_functional
  );
  const needSkillsBusiness = parseSkills(
    need?.skillsBusiness || need?.skills_business
  );

  const consultantSkillsTechnical = parseSkills(
    consultant?.skillsTechnical || consultant?.skills_technical
  );
  const consultantSkillsFunctional = parseSkills(
    consultant?.skillsFunctional || consultant?.skills_functional
  );
  const consultantSkillsBusiness = parseSkills(
    consultant?.skillsBusiness || consultant?.skills_business
  );

  const needId =
    need?.id || matchData?.needId || matchData?.need_id || matchData?.need?.id || null;

  const consultantId =
    consultant?.id ||
    matchData?.consultantId ||
    matchData?.consultant_id ||
    matchData?.consultant?.id ||
    null;

  const handleGoToNeedDetail = () => {
    if (!needId) return;
    navigate(`/portal/asap-pilot/needs/${needId}`);
  };

  const handleGoToConsultantDetail = () => {
    if (!consultantId) return;
    navigate(`/portal/asap-pilot/consultants/${consultantId}`);
  };

  // -- Loading state -----------------------------------------------------------
  if (loadingMatch) {
    return (
      <Box mt="-24px" mx="-24px" mb="-24px" minH="100%" display="flex" flexDirection="column">
        <PageHeader
          moduleCode="PI"
          eyebrow={t("pilotMatches.list.eyebrow", "ASAP PILOT")}
          title={t("pilotMatches.detail.pageTitle", { id: matchId })}
        />
        <Center flex="1" py="60px">
          <Spinner color="portal.primary" />
        </Center>
      </Box>
    );
  }

  // -- Not-found / error state -------------------------------------------------
  if (!matchData) {
    return (
      <Box mt="-24px" mx="-24px" mb="-24px" minH="100%" display="flex" flexDirection="column">
        <PageHeader
          moduleCode="PI"
          eyebrow={t("pilotMatches.list.eyebrow", "ASAP PILOT")}
          title={t("pilotMatches.detail.notFound")}
          actions={
            <Button
              variant="secondary"
              size="sm"
              h="32px"
              px="12px"
              fontSize="12px"
              leftIcon={<ArrowBackIcon />}
              onClick={() => navigate("/portal/asap-pilot/matches")}
            >
              {t("pilotMatches.detail.back")}
            </Button>
          }
        />
        <Box p="20px" flex="1">
          <Panel>
            <EmptyState
              icon={<FaCalendarCheck />}
              title={t("pilotMatches.detail.notFound")}
              hint={t("pilotMatches.detail.notFoundHint", {
                defaultValue: "This match may have been deleted or is unavailable.",
              })}
            />
          </Panel>
        </Box>
      </Box>
    );
  }

  // -- Header bits -------------------------------------------------------------
  const scoreBadge =
    matchData.score != null ? (
      <StatusBadge
        label={t("pilotMatches.detail.scoreBadge", {
          defaultValue: "Score {{score}}",
          score: matchData.score,
        })}
        colorScheme="blue"
      />
    ) : null;

  const headerActions = (
    <HStack spacing="8px">
      <Button
        variant="secondary"
        size="sm"
        h="32px"
        px="12px"
        fontSize="12px"
        leftIcon={<ArrowBackIcon />}
        onClick={() => navigate("/portal/asap-pilot/matches")}
        aria-label={t("pilotMatches.detail.backAria")}
      >
        {t("pilotMatches.detail.back")}
      </Button>
      <Button
        variant="primary"
        size="sm"
        h="32px"
        px="12px"
        fontSize="12px"
        onClick={trackingModal.onOpen}
      >
        {t("pilotMatches.detail.missionTracking.actions.open")}
      </Button>
      {canDelete && (
        <Button
          variant="secondary"
          size="sm"
          h="32px"
          px="12px"
          fontSize="12px"
          color="portal.danger"
          leftIcon={<DeleteIcon />}
          onClick={deleteConfirmDialog.onOpen}
        >
          {t("pilotMatches.detail.actions.delete")}
        </Button>
      )}
    </HStack>
  );

  const interviewColumns = [
    { key: "id", label: t("pilotMatches.detail.fields.id"), width: "70px" },
    { key: "status", label: t("pilotMatches.detail.interviews.fields.status"), width: "26%" },
    { key: "scheduledAt", label: t("pilotMatches.detail.interviews.fields.scheduledAt") },
    { key: "completedAt", label: t("pilotMatches.detail.interviews.fields.completedAt") },
    {
      key: "actions",
      label: t("pilotMatches.detail.table.actions"),
      width: "70px",
      align: "right",
    },
  ];

  return (
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%" display="flex" flexDirection="column">
      <PageHeader
        moduleCode="PI"
        eyebrow={t("pilotMatches.list.eyebrow", "ASAP PILOT")}
        title={
          <Flex align="center" gap="10px" flexWrap="wrap">
            <Box as="span">
              {t("pilotMatches.detail.pageTitle", { id: matchData.id || matchId })}
            </Box>
            {scoreBadge}
          </Flex>
        }
        actions={headerActions}
      />

      <Box p="20px" flex="1" minH="0" display="flex" flexDirection="column" gap="16px">
        {/* KPI strip — match figures */}
        <KpiStrip>
          <KpiCard
            label={t("pilotMatches.detail.fields.score")}
            value={matchData.score != null ? matchData.score : "N/A"}
          />
          <KpiCard
            label={t("pilotMatches.detail.fields.atsScore", { defaultValue: "ATS score" })}
            value={matchData.atsScore ?? matchData.ats_score ?? "N/A"}
          />
          <KpiCard
            label={t("pilotMatches.detail.fields.skillsScore", { defaultValue: "Skills score" })}
            value={matchData.skillsScore ?? matchData.skills_score ?? "N/A"}
          />
          <KpiCard
            label={t("pilotMatches.detail.fields.source")}
            value={matchData.source || "N/A"}
          />
        </KpiStrip>

        {/* Reasoning panel */}
        <Panel title={t("pilotMatches.detail.sections.reasoning")} bodyProps={{ p: "14px 16px" }}>
          <Text
            fontSize="13px"
            color="portal.text"
            whiteSpace="pre-wrap"
            sx={{ marginBottom: "0 !important" }}
          >
            {matchData.reasoning || t("pilotMatches.detail.noReasoning")}
          </Text>
        </Panel>

        {/* Assignment tracking summary panel */}
        <Panel
          title={t("pilotMatches.detail.missionTracking.title")}
          actions={
            <Button
              variant="secondary"
              size="sm"
              h="28px"
              px="10px"
              fontSize="12px"
              leftIcon={<EditIcon />}
              onClick={trackingModal.onOpen}
            >
              {t("pilotMatches.detail.missionTracking.actions.open")}
            </Button>
          }
          bodyProps={{ p: "14px 16px" }}
        >
          <SimpleGrid columns={{ base: 1, sm: 2, lg: 3 }} spacingX="20px" spacingY="14px">
            <ReadField label={t("pilotMatches.detail.missionTracking.fields.assignmentStatus")}>
              {tracking.assignmentStatus ? (
                <StatusBadge
                  label={getStatusLabel(
                    t,
                    MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
                    MATCH_ASSIGNMENT_STATUS_LABELS,
                    tracking.assignmentStatus
                  )}
                  colorScheme={ASSIGNMENT_STATUS_COLORS[tracking.assignmentStatus] || "gray"}
                />
              ) : (
                "-"
              )}
            </ReadField>
            <ReadField label={t("pilotMatches.detail.missionTracking.fields.purchaseOrderStatus")}>
              {tracking.purchaseOrderStatus ? (
                <StatusBadge
                  label={getStatusLabel(
                    t,
                    MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
                    MATCH_PURCHASE_ORDER_STATUS_LABELS,
                    tracking.purchaseOrderStatus
                  )}
                  colorScheme={PURCHASE_ORDER_STATUS_COLORS[tracking.purchaseOrderStatus] || "gray"}
                />
              ) : (
                "-"
              )}
            </ReadField>
            <ReadField label={t("pilotMatches.detail.missionTracking.fields.assignmentStartDate")}>
              {tracking.assignmentStartDate || "-"}
            </ReadField>
            <ReadField label={t("pilotMatches.detail.missionTracking.fields.assignmentEndDate")}>
              {tracking.assignmentEndDate || "-"}
            </ReadField>
            <ReadField label={t("pilotMatches.detail.missionTracking.fields.purchaseOrderNumber")}>
              {tracking.purchaseOrderNumber || "-"}
            </ReadField>
            <ReadField label={t("pilotMatches.detail.missionTracking.fields.purchaseOrderReceivedAt")}>
              {formatDatetimeLocalForDisplay(tracking.purchaseOrderReceivedAt)}
            </ReadField>
            <ReadField
              label={t("pilotMatches.detail.missionTracking.fields.purchaseOrderEndDate", {
                defaultValue: "Purchase order end date",
              })}
            >
              {tracking.purchaseOrderEndDate || "-"}
            </ReadField>
            <ReadField
              label={t("pilotMatches.detail.missionTracking.fields.clientSatisfactionScore")}
            >
              {tracking.clientSatisfactionScore !== "" &&
              tracking.clientSatisfactionScore != null ? (
                <StatusBadge
                  label={String(tracking.clientSatisfactionScore)}
                  colorScheme="purple"
                />
              ) : (
                "-"
              )}
            </ReadField>
            <ReadField
              label={t("pilotMatches.detail.missionTracking.fields.clientSatisfactionComment")}
            >
              <Box as="span" whiteSpace="pre-wrap" fontWeight="600">
                {tracking.clientSatisfactionComment || "-"}
              </Box>
            </ReadField>
          </SimpleGrid>
        </Panel>

        {/* Interviews panel — DataTable */}
        <Panel
          title={t("pilotMatches.detail.interviews.title")}
          count={interviews.length}
          actions={
            <Button
              variant="primary"
              size="sm"
              h="28px"
              px="10px"
              fontSize="12px"
              leftIcon={<AddIcon />}
              onClick={openCreateInterview}
            >
              {t("pilotMatches.detail.interviews.actions.add")}
            </Button>
          }
          bodyProps={{ p: 0 }}
        >
          <DataTable
            columns={interviewColumns}
            rows={interviews}
            loading={loadingInterviews}
            emptyState={
              <EmptyState
                icon={<FaCalendarCheck />}
                title={t("pilotMatches.detail.interviews.emptyState")}
              />
            }
            renderRow={(interview) => (
              <Tr key={interview.id}>
                <Td>#{interview.id}</Td>
                <Td>
                  {interview.status ? (
                    <StatusBadge
                      label={getStatusLabel(
                        t,
                        INTERVIEW_STATUS_LABEL_KEYS,
                        INTERVIEW_STATUS_LABELS,
                        interview.status
                      )}
                      colorScheme={INTERVIEW_STATUS_COLORS[interview.status] || "gray"}
                    />
                  ) : (
                    "-"
                  )}
                </Td>
                <Td>{formatInterviewDatetime(interview.scheduledAt)}</Td>
                <Td>{formatInterviewDatetime(interview.completedAt)}</Td>
                <Td textAlign="right">
                  <Tooltip
                    label={t("pilotMatches.detail.interviews.actions.edit")}
                    placement="top"
                    hasArrow
                  >
                    <Box as="span">
                      <MiniIconButton
                        aria-label={t("pilotMatches.detail.interviews.actions.edit")}
                        icon={<EditIcon />}
                        onClick={() => openEditInterview(interview)}
                      />
                    </Box>
                  </Tooltip>
                </Td>
              </Tr>
            )}
          />
        </Panel>

        {/* Need + consultant info panels */}
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing="16px">
          {/* Need main info */}
          <Panel
            title={t("pilotMatches.detail.sections.needInfo")}
            actions={
              needId ? (
                <Button
                  variant="secondary"
                  size="sm"
                  h="28px"
                  px="10px"
                  fontSize="12px"
                  onClick={handleGoToNeedDetail}
                >
                  {t("pilotMatches.detail.actions.viewNeed")}
                </Button>
              ) : null
            }
            bodyProps={{ p: "14px 16px" }}
          >
            {need ? (
              <SimpleGrid columns={1} spacing="14px">
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX="20px" spacingY="14px">
                  <ReadField label={t("pilotNeeds.detail.sections.client")}>
                    {need.clientName || "N/A"}
                  </ReadField>
                  <ReadField label={t("pilotNeeds.detail.sections.date")}>
                    {need.date || "N/A"}
                  </ReadField>
                  <ReadField label={t("pilotNeeds.detail.sections.status")}>
                    {getStatusLabel(t, NEED_STATUS_LABEL_KEYS, NEED_STATUS_LABELS, need.status) ||
                      "N/A"}
                  </ReadField>
                  <ReadField label={t("pilotNeeds.detail.sections.title")}>
                    {need.title || "N/A"}
                  </ReadField>
                </SimpleGrid>
                <ReadField label={t("pilotNeeds.detail.sections.description")}>
                  <Box as="span" whiteSpace="pre-wrap" fontWeight="600">
                    {need.description || t("pilotNeeds.detail.sections.noDescription")}
                  </Box>
                </ReadField>
                {needSkillsTechnical.length > 0 && (
                  <ReadField label={t("pilotNeeds.detail.sections.technicalSkills")}>
                    <HStack wrap="wrap" spacing="6px" mt="4px">
                      {needSkillsTechnical.map((skill, idx) => (
                        <SkillChip key={idx}>{skill}</SkillChip>
                      ))}
                    </HStack>
                  </ReadField>
                )}
                {needSkillsFunctional.length > 0 && (
                  <ReadField label={t("pilotNeeds.detail.sections.functionalSkills")}>
                    <HStack wrap="wrap" spacing="6px" mt="4px">
                      {needSkillsFunctional.map((skill, idx) => (
                        <SkillChip key={idx}>{skill}</SkillChip>
                      ))}
                    </HStack>
                  </ReadField>
                )}
                {needSkillsBusiness.length > 0 && (
                  <ReadField label={t("pilotNeeds.detail.sections.businessSkills")}>
                    <HStack wrap="wrap" spacing="6px" mt="4px">
                      {needSkillsBusiness.map((skill, idx) => (
                        <SkillChip key={idx}>{skill}</SkillChip>
                      ))}
                    </HStack>
                  </ReadField>
                )}
              </SimpleGrid>
            ) : (
              <EmptyState
                title={t("pilotMatches.detail.noNeedInContext")}
              />
            )}
          </Panel>

          {/* Consultant main info */}
          <Panel
            title={t("pilotMatches.detail.sections.consultantInfo")}
            actions={
              consultantId ? (
                <Button
                  variant="secondary"
                  size="sm"
                  h="28px"
                  px="10px"
                  fontSize="12px"
                  onClick={handleGoToConsultantDetail}
                >
                  {t("pilotMatches.detail.actions.viewConsultant")}
                </Button>
              ) : null
            }
            bodyProps={{ p: "14px 16px" }}
          >
            {loadingConsultant ? (
              <Center py="20px">
                <Spinner size="sm" color="portal.primary" />
              </Center>
            ) : consultant ? (
              <SimpleGrid columns={1} spacing="14px">
                <SimpleGrid columns={{ base: 1, sm: 2 }} spacingX="20px" spacingY="14px">
                  <ReadField label={t("pilotConsultants.detail.sections.title")}>
                    {consultant.title || "-"}
                  </ReadField>
                  <ReadField label={t("pilotConsultants.detail.sections.email")}>
                    {consultant.email || "-"}
                  </ReadField>
                  <ReadField label={t("pilotConsultants.detail.sections.phone")}>
                    {consultant.phone || "-"}
                  </ReadField>
                  <ReadField label={t("pilotConsultants.detail.sections.location")}>
                    {consultant.location || "-"}
                  </ReadField>
                  <ReadField label={t("pilotConsultants.detail.sections.availability")}>
                    {consultant.availability || "-"}
                  </ReadField>
                  <ReadField label={t("pilotConsultants.detail.sections.status")}>
                    {getStatusLabel(
                      t,
                      CONSULTANT_STATUS_LABEL_KEYS,
                      CONSULTANT_STATUS_LABELS,
                      consultant.status
                    ) || "-"}
                  </ReadField>
                </SimpleGrid>
                {consultant.description && (
                  <ReadField label={t("pilotConsultants.detail.sections.description")}>
                    <Box as="span" whiteSpace="pre-wrap" fontWeight="600">
                      {consultant.description}
                    </Box>
                  </ReadField>
                )}
                {consultantSkillsTechnical.length > 0 && (
                  <ReadField label={t("pilotConsultants.detail.sections.technicalSkills")}>
                    <HStack wrap="wrap" spacing="6px" mt="4px">
                      {consultantSkillsTechnical.map((skill, idx) => (
                        <SkillChip key={idx}>{skill}</SkillChip>
                      ))}
                    </HStack>
                  </ReadField>
                )}
                {consultantSkillsFunctional.length > 0 && (
                  <ReadField label={t("pilotConsultants.detail.sections.functionalSkills")}>
                    <HStack wrap="wrap" spacing="6px" mt="4px">
                      {consultantSkillsFunctional.map((skill, idx) => (
                        <SkillChip key={idx}>{skill}</SkillChip>
                      ))}
                    </HStack>
                  </ReadField>
                )}
                {consultantSkillsBusiness.length > 0 && (
                  <ReadField label={t("pilotConsultants.detail.sections.businessSkills")}>
                    <HStack wrap="wrap" spacing="6px" mt="4px">
                      {consultantSkillsBusiness.map((skill, idx) => (
                        <SkillChip key={idx}>{skill}</SkillChip>
                      ))}
                    </HStack>
                  </ReadField>
                )}
              </SimpleGrid>
            ) : (
              <EmptyState
                title={t("pilotMatches.detail.noConsultantInContext")}
              />
            )}
          </Panel>
        </SimpleGrid>
      </Box>

      {/* Assignment tracking modal */}
      <Modal isOpen={trackingModal.isOpen} onClose={trackingModal.onClose} size="2xl">
        <ModalOverlay />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotMatches.detail.missionTracking.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <Text fontSize="12px" color="portal.muted" mb="14px" sx={{ marginBottom: "14px !important" }}>
              {t("pilotMatches.detail.missionTracking.description")}
            </Text>

            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.assignmentStatus")}
                </FieldLabel>
                <Select
                  value={tracking.assignmentStatus}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, assignmentStatus: e.target.value }))
                  }
                  sx={TOKEN_SELECT_SX}
                >
                  <option value="">
                    {t("pilotMatches.detail.missionTracking.fields.empty")}
                  </option>
                  {MATCH_ASSIGNMENT_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        MATCH_ASSIGNMENT_STATUS_LABEL_KEYS,
                        MATCH_ASSIGNMENT_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderStatus")}
                </FieldLabel>
                <Select
                  value={tracking.purchaseOrderStatus}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderStatus: e.target.value }))
                  }
                  sx={TOKEN_SELECT_SX}
                >
                  <option value="">
                    {t("pilotMatches.detail.missionTracking.fields.empty")}
                  </option>
                  {MATCH_PURCHASE_ORDER_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        MATCH_PURCHASE_ORDER_STATUS_LABEL_KEYS,
                        MATCH_PURCHASE_ORDER_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.assignmentStartDate")}
                </FieldLabel>
                <Input
                  type="date"
                  value={tracking.assignmentStartDate}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, assignmentStartDate: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.assignmentEndDate")}
                </FieldLabel>
                <Input
                  type="date"
                  value={tracking.assignmentEndDate}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, assignmentEndDate: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderNumber")}
                </FieldLabel>
                <Input
                  value={tracking.purchaseOrderNumber}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderNumber: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderReceivedAt")}
                </FieldLabel>
                <Input
                  type="datetime-local"
                  value={tracking.purchaseOrderReceivedAt}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderReceivedAt: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.purchaseOrderEndDate", {
                    defaultValue: "Purchase order end date",
                  })}
                </FieldLabel>
                <Input
                  type="date"
                  value={tracking.purchaseOrderEndDate}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, purchaseOrderEndDate: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.fields.atsScore", { defaultValue: "ATS score" })}
                </FieldLabel>
                <Input
                  type="number"
                  step="0.01"
                  value={tracking.atsScore}
                  onChange={(e) => setTracking((s) => ({ ...s, atsScore: e.target.value }))}
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.fields.skillsScore", { defaultValue: "Skills score" })}
                </FieldLabel>
                <Input
                  type="number"
                  step="0.01"
                  value={tracking.skillsScore}
                  onChange={(e) => setTracking((s) => ({ ...s, skillsScore: e.target.value }))}
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.clientSatisfactionScore")}
                </FieldLabel>
                <Input
                  type="number"
                  min={1}
                  max={10}
                  step={1}
                  value={tracking.clientSatisfactionScore}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === "") {
                      setTracking((s) => ({ ...s, clientSatisfactionScore: value }));
                      return;
                    }
                    const numericValue = Number(value);
                    if (!Number.isInteger(numericValue)) return;
                    if (numericValue < 1 || numericValue > 10) return;
                    setTracking((s) => ({ ...s, clientSatisfactionScore: value }));
                  }}
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl gridColumn={{ md: "1 / -1" }}>
                <FieldLabel>
                  {t("pilotMatches.detail.missionTracking.fields.clientSatisfactionComment")}
                </FieldLabel>
                <Textarea
                  rows={3}
                  fontSize="13px"
                  value={tracking.clientSatisfactionComment}
                  onChange={(e) =>
                    setTracking((s) => ({ ...s, clientSatisfactionComment: e.target.value }))
                  }
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" onClick={trackingModal.onClose}>
              {t("pilotMatches.detail.missionTracking.actions.close")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                await handleSaveTracking();
                trackingModal.onClose();
              }}
              isLoading={savingTracking}
            >
              {t("pilotMatches.detail.missionTracking.actions.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Interview modal */}
      <Modal
        isOpen={interviewModal.isOpen}
        onClose={() => {
          interviewModal.onClose();
          resetInterviewForm();
        }}
        size="lg"
      >
        <ModalOverlay />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {t("pilotMatches.detail.interviews.title")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody py="16px">
            <Text fontSize="12px" color="portal.muted" mb="14px" sx={{ marginBottom: "14px !important" }}>
              {t("pilotMatches.detail.interviews.description")}
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing="14px">
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.interviews.fields.status")}
                </FieldLabel>
                <Select
                  value={interviewForm.status}
                  onChange={(e) =>
                    setInterviewForm((s) => ({ ...s, status: e.target.value }))
                  }
                  sx={TOKEN_SELECT_SX}
                >
                  <option value="">
                    {t("pilotMatches.detail.interviews.fields.empty")}
                  </option>
                  {INTERVIEW_STATUSES.map((status) => (
                    <option key={status.value} value={status.value}>
                      {getStatusLabel(
                        t,
                        INTERVIEW_STATUS_LABEL_KEYS,
                        INTERVIEW_STATUS_LABELS,
                        status.value
                      )}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.interviews.fields.scheduledAt")}
                </FieldLabel>
                <Input
                  type="datetime-local"
                  value={interviewForm.scheduledAt}
                  onChange={(e) =>
                    setInterviewForm((s) => ({ ...s, scheduledAt: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
              <FormControl>
                <FieldLabel>
                  {t("pilotMatches.detail.interviews.fields.completedAt")}
                </FieldLabel>
                <Input
                  type="datetime-local"
                  value={interviewForm.completedAt}
                  onChange={(e) =>
                    setInterviewForm((s) => ({ ...s, completedAt: e.target.value }))
                  }
                  sx={TOKEN_INPUT_SX}
                />
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                interviewModal.onClose();
                resetInterviewForm();
              }}
            >
              {t("pilotMatches.detail.interviews.actions.close")}
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={async () => {
                const saved = await handleSaveInterview();
                if (saved) {
                  interviewModal.onClose();
                  resetInterviewForm();
                }
              }}
              isLoading={savingInterview}
            >
              {t("pilotMatches.detail.interviews.actions.save")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      <ConfirmDialog
        isOpen={deleteConfirmDialog.isOpen}
        onClose={deleteConfirmDialog.onClose}
        onConfirm={handleDelete}
        title={t("pilotMatches.detail.confirmDeleteTitle", { defaultValue: "Delete match?" })}
        body={t("pilotMatches.detail.confirmDelete")}
        confirmLabel={t("common.delete", { defaultValue: "Delete" })}
      />
    </Box>
  );
};

export default MatchDetail;
